// Typed localStorage wrapper. Only this module touches window.localStorage.
//
// URL_PARAM_SECRET is intentionally absent from the keys list — per
// CLIENT-DECISION-004 it lives only in memory (the Zustand authStore).
//
// Realm namespacing:
//   - Tenant (staff/driver) tokens live in `accessToken` / `refreshToken`
//     (storage keys `tricivo_access_token` / `tricivo_refresh_token`).
//   - Platform super-admin tokens live in `superAccessToken` /
//     `superRefreshToken` (storage keys `tricivo_super_access_token` /
//     `tricivo_super_refresh_token`). Separate slots are mandatory: with
//     the old shared slots, logging into one realm in one tab evicted the
//     other realm's session in another tab.

const KEYS = {
  accessToken: 'tricivo_access_token',
  refreshToken: 'tricivo_refresh_token',
  realm: 'tricivo_realm',
  companyId: 'tricivo_company_id',
  superAccessToken: 'tricivo_super_access_token',
  superRefreshToken: 'tricivo_super_refresh_token',
  // Weighbridge Kiosk Mode — the weighbridge this device is locked to.
  // Deliberately NOT in TENANT_KEYS: a per-device physical lock that
  // survives staff logout (clearTenant) and only clears on full clear().
  kioskWeighbridge: 'tricivo_kiosk_weighbridge_enc_id',
} as const;

type StorageKey = keyof typeof KEYS;

const TENANT_KEYS: StorageKey[] = [
  'accessToken',
  'refreshToken',
  'realm',
  'companyId',
];

const SUPER_KEYS: StorageKey[] = ['superAccessToken', 'superRefreshToken'];

// Legacy localStorage keys that pre-dated the typed wrapper. Nothing in
// the current code writes them; the cleanup helper removes any stragglers
// left behind on existing browsers.
const LEGACY_KEYS = ['tricivo_token', 'tricivo_user'] as const;

function backing(): globalThis.Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export const storage = {
  get(key: StorageKey): string | null {
    const s = backing();
    if (!s) return null;
    try {
      return s.getItem(KEYS[key]);
    } catch {
      return null;
    }
  },

  set(key: StorageKey, value: string): void {
    const s = backing();
    if (!s) return;
    try {
      s.setItem(KEYS[key], value);
    } catch {
      // Quota or privacy-mode failures are non-fatal here.
    }
  },

  remove(key: StorageKey): void {
    const s = backing();
    if (!s) return;
    try {
      s.removeItem(KEYS[key]);
    } catch {
      // ignore
    }
  },

  /** Clears every Tricivo-owned key (tenant + super + legacy). */
  clear(): void {
    const s = backing();
    if (!s) return;
    try {
      for (const k of Object.values(KEYS)) s.removeItem(k);
      for (const k of LEGACY_KEYS) s.removeItem(k);
    } catch {
      // ignore
    }
  },

  /**
   * Clears only the tenant (staff/driver) keys. Use on tenant logout so a
   * concurrent super-admin session in the same browser is preserved.
   */
  clearTenant(): void {
    const s = backing();
    if (!s) return;
    try {
      for (const k of TENANT_KEYS) s.removeItem(KEYS[k]);
    } catch {
      // ignore
    }
  },

  /**
   * Clears only the super-admin keys. Use on super logout so a concurrent
   * tenant session in the same browser is preserved.
   */
  clearSuper(): void {
    const s = backing();
    if (!s) return;
    try {
      for (const k of SUPER_KEYS) s.removeItem(KEYS[k]);
    } catch {
      // ignore
    }
  },

  /**
   * Removes any pre-typed-wrapper localStorage keys. Idempotent. Stores
   * call this once on creation so browsers that booted before the typed
   * wrapper landed don't keep dead keys around.
   */
  removeLegacyKeys(): void {
    const s = backing();
    if (!s) return;
    try {
      for (const k of LEGACY_KEYS) s.removeItem(k);
    } catch {
      // ignore
    }
  },

  // Internal-only — exposed for tests so they can inspect the key map.
  _keys: KEYS,
};

export type AuthStorage = typeof storage;
